import {
	Component,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	HostListener,
	ElementRef,
	OnInit
} from '@angular/core';
import { max } from 'd3-array';
import { scaleLinear, scaleBand, ScaleBand, ScaleLinear } from 'd3-scale';
import { NgIf, NgFor, NgClass } from '@angular/common';

import { safe } from '../../../utils/safe';
import { BarData } from '../../../models/bar-data';
import { NumberPipe } from '../../../pipes/number.pipe';

@Component({
	selector: 'copo-bars',
	templateUrl: './bars.component.html',
	styleUrl: './bars.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [NgIf, NgFor, NgClass, NumberPipe]
})
export class BarsComponent implements OnInit {
	@Input() barData?: BarData[];
	@Output() readonly barClicked = new EventEmitter<number>();
	margin = { top: 50, right: 20, bottom: 50, left: 20 };
	width = 0;
	height = 0;
	readonly r = 5;

	x?: ScaleBand<string>;
	y?: ScaleLinear<number, number>;

	xVert?: ScaleLinear<number, number>;
	yVert?: ScaleBand<string>;

	largeScreen = true;

	constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

	@HostListener('window:resize') onResize() {
		this.calcGraphDimension();
	}

	ngOnInit() {
		this.calcGraphDimension();
	}

	calcGraphDimension() {
		const { parentElement } = this.elementRef.nativeElement;
		this.largeScreen = innerWidth > 768;
		this.margin = { top: 50, right: 20, bottom: 50, left: 20 };
		this.height = 50;
		if (!this.largeScreen) {
			this.margin.left += 80;
			this.margin.top -= 30;
			this.margin.bottom -= 30;
			this.height = 320;
		}
		this.width = safe(parentElement).offsetWidth - this.margin.left - this.margin.right;
		this.update();
	}

	update() {
		if (!this.barData) {
			return;
		}

		this.x = scaleBand()
			.domain(this.barData.map(v => v.title))
			.range([0, this.width])
			.padding(0.6);

		this.y = scaleLinear()
			.domain([0, safe(max(this.barData, d => d.value))])
			.range([this.height, 0]);

		this.xVert = scaleLinear()
			.domain([0, safe(max(this.barData, d => d.value))])
			.range([this.width, 0]);

		this.yVert = scaleBand()
			.domain(this.barData.map(v => v.title))
			.range([0, this.height])
			.padding(0.4);
	}

	roundedRect(
		x: number,
		y: number,
		w: number,
		h: number,
		r: number,
		tl: boolean,
		tr: boolean,
		bl: boolean,
		br: boolean
	) {
		let retval;
		retval = `M${ x + r },${ y }`;
		retval += `h${ w - (r * 2) }`;
		if (tr) {
			retval += `a${ r },${ r } 0 0 1 ${ r },${ r }`;
		} else {
			retval += `h${ r }`;
			retval += `v${ r }`;
		}
		retval += `v${ h - (r * 2) }`;
		if (br) {
			retval += `a${ r },${ r } 0 0 1 ${ -r },${ r }`;
		} else {
			retval += `v${ r }`;
			retval += `h${ -r }`;
		}
		retval += `h${ (r * 2) - w }`;
		if (bl) {
			retval += `a${ r },${ r } 0 0 1 ${ -r },${ -r }`;
		} else {
			retval += `h${ -r }`;
			retval += `v${ -r }`;
		}
		retval += `v${ (r * 2) - h }`;
		if (tl) {
			retval += `a${ r },${ r } 0 0 1 ${ r },${ -r }`;
		} else {
			retval += `v${ -r }`;
			retval += `h${ r }`;
		}
		retval += 'z';
		return retval;
	}

	barCenter(d: string, scale: ScaleBand<string>) {
		let offset = Math.max(0, scale.bandwidth() - 1) / 2;
		if (scale.round()) {
			offset = Math.round(offset);
		}
		return safe(scale(d)) + offset;
	}

	barClick(index: number, data: BarData) {
		if (data.value !== 0) {
			this.barClicked.emit(index);
		}
	}
}
